import { compact, List, uniq } from "lodash";
import { DateTime } from "luxon";
import randomBytes from "randombytes";
import { escapeRegexSpecialChars, REGEX_NON_ALPHANUMERIC } from "./regex";
import { MS_END, Timezone } from "./timezone";

export const toJSON = (obj: any): any => {
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (error) {
    console.error(error);
    return {};
  }
};

export const unique = <T>(array: List<T>) => {
  return uniq(compact(array));
};

export const walletAddressPreview = (address: string): string => {
  if (!address) return "";
  return `${address.substring(0, 8)}...${address.slice(address.length - 5)}`;
};

export const API_RETRY_HEADER = "MOONGATE-RETRY";

export const intToHex = (int: number): string => {
  return `0x${int.toString(16)}`;
};

export const hexToInt = (hex: string): number => {
  return parseInt(hex, 16);
};

export const toDuration = (
  start: number,
  end: number,
  timezone: Timezone
): string => {
  if (start == 0 && end == MS_END) return "Forever";
  const startDate = DateTime.fromMillis(start, { zone: timezone });
  const endDate = DateTime.fromMillis(end, { zone: timezone });
  const startStr = start == 0 ? "Now" : startDate.toFormat("d MMM yy, HH:mm");
  if (start == end)
    return startStr + (start > 0 ? ` (${startDate.offsetNameShort})` : "");
  const endStr =
    end == MS_END
      ? "Forever"
      : endDate.hasSame(startDate, "day")
      ? endDate.toFormat("HH:mm")
      : endDate.toFormat("d MMM yy, HH:mm");
  return `${startStr} - ${endStr} (${startDate.offsetNameShort})`;
};

export const formatDate = (
  timestamp: number,
  timezone = Timezone["Asia/Hong_Kong"],
  format = "d MMM yy, HH:mm"
) => {
  const date = DateTime.fromMillis(timestamp, { zone: timezone });
  return date.toFormat(format);
};

export const cryptoId = (length = 16) => {
  return randomBytes(length).toString("hex");
};

export const toSearchText = (text: string) => {
  return (text || "")
    .toLowerCase()
    .replace(REGEX_NON_ALPHANUMERIC, "")
    .replace(/\s+/g, "");
};

export const toSlug = (text: string) => {
  return (text || "")
    .replace(/[^a-z\d\s\-]/gi, "")
    .toLowerCase()
    .replace(/\s/g, "-")
    .replace(/\-+/g, "-")
    .trim();
};

export const compareText = (t1?: string, t2?: string) => {
  if (!t1 || !t2) return false;
  return normalizeId(t1 || "") === normalizeId(t2 || "");
};

export const normalizeId = (id: string) => {
  try {
    return id.trim().toLowerCase();
  } catch (error) {
    return id;
  }
};

export const normalizeEmail = (email: string) => {
  try {
    return normalizeId(email).replace(/\+.*@/, "@");
  } catch (error) {
    return email;
  }
};

export const normalizeIdentifier = (identifier: string) => {
  return normalizeEmail(identifier);
};

export const toRegexQueryArray = (identifiers: string[]) => {
  return identifiers.map(
    (i) => new RegExp(`^${escapeRegexSpecialChars(i)}$`, "im")
  );
};
